import {
  collectSignupAttribution,
  clearSignupAttribution,
  readGaClientId,
} from "./services/signupAttribution";
import {
  getCachedAffiliateIp,
  prefetchAffiliateIp,
} from "./services/affiliateIp";
import login from "./login";
import {
  getLocalStorage,
  getSessionItem,
  setSessionItem,
  removeSessionItem,
} from "./services/utility";
import {
  getSignupBillingType,
  DEFAULT_SELF_SERVE_TRIAL_TYPE,
  getExplicitSelfServeTrialType,
} from "./signupPlans";
import {
  classifyGoogleLinkError,
  GOOGLE_LINK_MESSAGES,
  isGoogleLinkTerminal,
  signupRecoveryMessage,
} from "./federatedLinkErrors";

const init = function () {
  /**
   *
   * @description Mobile Menu Handler
   *
   */

  const HOST_URL =
    typeof window !== "undefined" && window.__IS_WHITELABEL__
      ? window.location.origin
      : process.env.HOST_URL;
  const menu = document.getElementById("menu");
  const nav = document.getElementById("site-navbar");
  const hamburger = document.getElementById("menu-hamburger");
  const toggleClass = "show";

  menu &&
    menu.addEventListener("click", () => {
      hamburger.classList.toggle(toggleClass);
      nav.classList.toggle(toggleClass);
    });

  const signin = document.querySelector("#signin");
  signin &&
    signin.addEventListener("click", () => {
      location.href = "/login";
    });

  document.querySelectorAll(".signin-link").forEach((link) => {
    link.addEventListener("click", () => {
      window.location.href = "/login";
    });
  });

  /**
   * @description Login Triggers
   */

  const loginTriggers = document.querySelectorAll(".login-btn");
  loginTriggers.forEach((btn) => {
    btn.addEventListener(
      "click",
      () => {
        // eslint-disable-next-line no-undef
        ga("send", "event", "Button Click", "Trial", window.location.href, 1);
        window.location.href = "/signup";
      },
      false,
    );
  });

  const getQueryVar = (type) => {
    let hash;
    const hashes = window.location.href
      .slice(window.location.href.indexOf("?") + 1)
      .split("&");
    for (let i = 0; i < hashes.length; i++) {
      hash = hashes[i].split("=");
      if (hash[0] === type) {
        return hash[1];
      }
    }
    return null;
  };

  const onLogin = function onLogin(isTeamLogin = false, evt) {
    evt.preventDefault();
    const item = evt.currentTarget;
    const type = item.getAttribute("data-type");
    let packageType = item.getAttribute("data-package");
    let productType = item.getAttribute("data-product");
    if (type) {
      if (!packageType) {
        packageType = DEFAULT_SELF_SERVE_TRIAL_TYPE;
      }

      if (!productType) {
        productType = "PUBLISHER";
      }
      const couponCode = getQueryVar("couponCode");
      const lifetimeCode = getQueryVar("lifetimeCode");
      login(
        type,
        false,
        productType,
        packageType,
        couponCode,
        lifetimeCode,
        isTeamLogin,
      );
    }
  };

  const socialLoginTriggers = document.querySelectorAll("#social-auths button");
  socialLoginTriggers.forEach((btn) => {
    btn.addEventListener("click", onLogin.bind(null, false), false);
  });

  const federatedGoogleButton = document.getElementById(
    "federated-google-button",
  );
  if (
    federatedGoogleButton &&
    federatedGoogleButton.dataset.federatedGoogleInitialized !== "true"
  ) {
    federatedGoogleButton.dataset.federatedGoogleInitialized = "true";
    const isGoogleSignupIntent =
      federatedGoogleButton.dataset.googleIntent === "signup";
    // Third-party script: only on the signup page, never on the login page.
    if (isGoogleSignupIntent) {
      void prefetchAffiliateIp();
    }
    const googleSignupPanel = document.getElementById(
      "federated-google-signup",
    );
    const googleSignupConfirm = document.getElementById(
      "federated-google-signup-confirm",
    );
    const googleSignupEmail = document.getElementById(
      "federated-google-signup-email",
    );
    const googleSignupError = document.getElementById(
      "federated-google-signup-error",
    );
    const signupSwitch = document.getElementById(
      "federated-google-signup-switch",
    );
    let signupBusy = false;
    let signupRestart = false;
    let signupExit = "close";
    let googleProfile = {};
    let googleLinkReturnFocus = null;
    // history.replaceState cannot clear document.referrer after OAuth.
    const returnedFromGoogle = ["resume", "error", "cancelled"].includes(
      new URLSearchParams(window.location.search).get("federated"),
    );
    const trackSignup = (event, properties = {}) => {
      try {
        (window.dataLayer = window.dataLayer || []).push({
          event,
          ...properties,
        });
      } catch (_) {
        /* Analytics must not block signup. */
      }
    };
    const renderGoogleProfile = (mode, profile) => {
      const name = document.getElementById(`federated-google-${mode}-name`);
      if (name) {
        name.textContent = profile.name || "";
        name.classList.toggle("d-none", !profile.name);
      }
      const avatar = document.getElementById(`federated-google-${mode}-avatar`);
      if (!avatar) return;
      const initials = String(profile.name || profile.email || "G")
        .split(/[\s@]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
      avatar.textContent = initials;
      try {
        const url = new URL(profile.picture);
        if (
          url.protocol !== "https:" ||
          !(
            url.hostname === "googleusercontent.com" ||
            url.hostname.endsWith(".googleusercontent.com")
          )
        )
          return;
        const img = document.createElement("img");
        img.alt = "";
        img.referrerPolicy = "no-referrer";
        img.style.cssText =
          "width:100%;height:100%;object-fit:cover;border-radius:inherit";
        img.onerror = () => {
          avatar.textContent = initials;
        };
        img.src = url.href;
        avatar.textContent = "";
        avatar.appendChild(img);
      } catch (_) {
        /* Keep initials for missing or invalid photos. */
      }
    };
    const signupDescription = document.getElementById(
      "federated-google-signup-description",
    );
    if (signupDescription)
      signupDescription.textContent = isGoogleSignupIntent
        ? "Create your account using the Google email below."
        : "We couldn’t find a Social Champ account with this Google email. Create an account to continue.";
    let googleSignupContinuation = null;
    let googleSignupRecoveryUri = null;
    const googleLinkPanel = document.getElementById("federated-google-link");
    const googleLinkForm = document.getElementById(
      "federated-google-link-form",
    );
    const googleLinkClose = document.getElementById(
      "federated-google-link-close",
    );
    const googleLinkEmail = document.getElementById(
      "federated-google-link-email",
    );
    const googleLinkAvatar = document.getElementById(
      "federated-google-link-avatar",
    );
    const googleLinkTitle = document.getElementById(
      "federated-google-link-title",
    );
    const googleLinkDescription = document.getElementById(
      "federated-google-link-description",
    );
    const googleLinkMatchedAccount = document.getElementById(
      "federated-google-matched-account",
    );
    const googleLinkDifferentAccount = document.getElementById(
      "federated-google-different-account",
    );
    const googleLinkEmailInput = document.getElementById(
      "federated-google-link-email-input",
    );
    const googleLinkPassword = document.getElementById(
      "federated-google-link-password",
    );
    const googleLinkPasswordToggle = document.getElementById(
      "federated-google-link-password-toggle",
    );
    const googleLinkVerify = document.getElementById(
      "federated-google-link-verify",
    );
    const googleLinkCancel = document.getElementById(
      "federated-google-link-cancel",
    );
    const googleLinkError = document.getElementById(
      "federated-google-link-error",
    );
    let googleLinkContinuation = getSessionItem("federatedLinkContinuation");
    let googleLinkReadyToConfirm = Boolean(googleLinkContinuation);
    let googleLinkUsesDifferentAccount = false;
    const requestFederated = async (path, body, headers = {}) => {
      const response = await fetch(`${HOST_URL}${path}`, {
        method: body ? "POST" : "GET",
        credentials: "same-origin",
        headers: body
          ? {
              "Content-Type": "application/json",
              // Login experiment arm (z94kydcrz3), for the Google sign-up
              // event; "none" on a page that is not counted.
              "X-Login-Variant": window.__LOGIN_VARIANT__ || "none",
              ...headers,
            }
          : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      const payload = await response.json();
      if (!response.ok) {
        const error = new Error(payload?.message || "Unable to sign in.");
        error.payload = payload;
        error.status = response.status;
        throw error;
      }
      return payload;
    };
    const redirectFromFederatedResponse = (response) => {
      if (response?.data?.redirectUri)
        window.location.assign(response.data.redirectUri);
    };
    const resetGoogleSignup = () => {
      googleSignupContinuation = null;
      googleSignupRecoveryUri = null;
      signupRestart = false;
      if (googleSignupConfirm)
        googleSignupConfirm.textContent = "Create account";
      if (googleSignupError) {
        googleSignupError.textContent = "";
        googleSignupError.classList.add("d-none");
      }
      if (window.$) window.$(googleSignupPanel).modal("hide");
    };
    const showGoogleSignupRecovery = (retryUri) => {
      googleSignupContinuation = null;
      googleSignupRecoveryUri = retryUri;
      googleSignupConfirm.textContent = "Retry account setup";
      if (googleSignupError) {
        googleSignupError.textContent =
          "Your account was created, but setup isn’t finished. Please try again to complete it.";
        googleSignupError.classList.remove("d-none");
      }
    };
    const resetGoogleLink = () => {
      googleLinkContinuation = null;
      googleLinkReadyToConfirm = false;
      googleLinkUsesDifferentAccount = false;
      removeSessionItem("federatedLinkContinuation");
      if (window.$) window.$(googleLinkPanel).modal("hide");
      if (googleLinkPassword) {
        googleLinkPassword.value = "";
        googleLinkPassword.type = "password";
      }
      if (googleLinkPasswordToggle) {
        const passwordIcon = googleLinkPasswordToggle.querySelector("img");
        if (passwordIcon) passwordIcon.src = "/assets/images/eye-close.svg";
        googleLinkPasswordToggle.setAttribute("aria-label", "Show password");
      }
      if (googleLinkEmailInput) googleLinkEmailInput.value = "";
      if (googleLinkError) {
        googleLinkError.textContent = "";
        googleLinkError.classList.add("d-none");
      }
    };
    const setGoogleLinkError = (message) => {
      if (googleLinkError) {
        googleLinkError.textContent = message;
        googleLinkError.classList.toggle("d-none", !message);
      }
    };
    const setFederatedAlert = (message) => {
      const alert =
        document.getElementById("signin-alert") ||
        document.getElementById("signup-alert");
      const text = alert?.querySelector(".text");
      if (text) text.textContent = message;
      alert?.classList.remove("d-none");
    };
    const setGoogleLinkAccountMode = (useDifferentAccount) => {
      googleLinkUsesDifferentAccount = useDifferentAccount;
      googleLinkMatchedAccount?.classList.toggle("d-none", useDifferentAccount);
      googleLinkDifferentAccount?.classList.toggle(
        "d-none",
        !useDifferentAccount,
      );
      if (googleLinkDescription) {
        googleLinkDescription.textContent = useDifferentAccount
          ? "Enter the email and password of the Social Champ account you want to connect to Google. You'll then be able to sign in to it with Google."
          : googleLinkDescription.dataset.matchedCopy ||
            "You already have a Social Champ account with this email. Enter your Social Champ password to connect Google and sign in.";
      }
      if (googleLinkTitle)
        googleLinkTitle.textContent = useDifferentAccount
          ? "Connect Google to your account"
          : "Connect Google to your account";
    };
    const showGoogleLinkPanel = (email, useDifferentAccount = false) => {
      if (googleLinkEmail)
        googleLinkEmail.textContent =
          email || "the email on your Google account";
      if (googleLinkAvatar) {
        const localPart = String(email || "Google").split("@")[0];
        const nameParts = localPart.split(/[^A-Za-z0-9]+/).filter(Boolean);
        googleLinkAvatar.textContent = (
          nameParts.length > 1
            ? `${nameParts[0][0]}${nameParts[1][0]}`
            : localPart.slice(0, 2)
        ).toUpperCase();
      }
      renderGoogleProfile("link", { ...googleProfile, email });
      setGoogleLinkAccountMode(useDifferentAccount);
      setGoogleLinkError("");
      if (window.$) window.$(googleLinkPanel).modal("show");
    };
    const completeGoogleLink = async () => {
      if (!googleLinkContinuation) return;
      googleLinkVerify.disabled = true;
      googleLinkVerify.textContent = "Connecting Google…";
      googleLinkVerify.setAttribute?.("aria-busy", "true");
      setGoogleLinkError("");
      try {
        const confirmed = await requestFederated(
          "/oauth/v1/federated/link/confirm",
          { continuation: googleLinkContinuation },
        );
        resetGoogleLink();
        redirectFromFederatedResponse(confirmed);
      } catch (error) {
        // Decision logic lives in federatedLinkErrors.js so it can be unit
        // tested; this branch only applies the outcome to the DOM.
        const outcome = classifyGoogleLinkError(error?.status);
        const message = GOOGLE_LINK_MESSAGES[outcome];
        if (isGoogleLinkTerminal(outcome)) {
          // Drop the spent continuation so it stops re-firing the modal on
          // every /login load, and say why rather than closing in silence.
          resetGoogleLink();
          setFederatedAlert(message);
          return;
        }
        googleLinkReadyToConfirm = true;
        setGoogleLinkError(message);
        if (window.$) window.$(googleLinkPanel).modal("show");
      } finally {
        googleLinkVerify.disabled = false;
        // The login-experiment modal (z94kydcrz3) carries its own label.
        googleLinkVerify.textContent =
          googleLinkVerify.dataset.idleLabel || "Connect Google and sign in";
        googleLinkVerify.setAttribute?.("aria-busy", "false");
      }
    };
    googleLinkCancel?.addEventListener("click", () => {
      const selectedEmail = googleLinkUsesDifferentAccount
        ? googleLinkEmailInput?.value?.trim()
        : googleLinkEmail?.textContent?.trim();
      googleLinkReturnFocus = !isGoogleSignupIntent
        ? document.querySelector("#form-signin input[name=password]")
        : null;
      resetGoogleLink();
      if (isGoogleSignupIntent) {
        window.location.assign("/login");
        return;
      }
      const localEmail = document.querySelector(
        "#form-signin input[name=email]",
      );
      const localPassword = document.querySelector(
        "#form-signin input[name=password]",
      );
      if (selectedEmail && localEmail) localEmail.value = selectedEmail;
      if (!window.$) localPassword?.focus();
    });
    googleLinkClose?.addEventListener("click", resetGoogleLink);
    googleLinkPasswordToggle?.addEventListener("click", () => {
      if (!googleLinkPassword) return;
      const showPassword = googleLinkPassword.type === "password";
      googleLinkPassword.type = showPassword ? "text" : "password";
      const icon = googleLinkPasswordToggle.querySelector("img");
      if (icon)
        icon.src = showPassword
          ? "/assets/images/eye-open.svg"
          : "/assets/images/eye-close.svg";
      googleLinkPasswordToggle.setAttribute(
        "aria-label",
        showPassword ? "Hide password" : "Show password",
      );
    });
    googleLinkForm?.addEventListener("submit", async () => {
      if (!googleLinkContinuation) return;
      if (googleLinkReadyToConfirm) return completeGoogleLink();
      if (!googleLinkPassword?.value) {
        setGoogleLinkError("Enter your Social Champ password to continue.");
        return;
      }
      const differentEmail = googleLinkEmailInput?.value?.trim();
      if (googleLinkUsesDifferentAccount && !differentEmail) {
        setGoogleLinkError(
          "Enter the email address of your Social Champ account.",
        );
        return;
      }
      googleLinkVerify.disabled = true;
      googleLinkVerify.textContent = "Connecting Google…";
      googleLinkVerify.setAttribute?.("aria-busy", "true");
      setGoogleLinkError("");
      try {
        const link = await requestFederated(
          "/oauth/v1/federated/link/password",
          {
            continuation: googleLinkContinuation,
            password: googleLinkPassword.value,
            ...(googleLinkUsesDifferentAccount
              ? { email: differentEmail }
              : {}),
          },
        );
        if (link?.data?.requires2FA) {
          // The 2FA page reads this continuation back out of sessionStorage to
          // finish the link. If the write fails - storage blocked by policy, a
          // sandboxed frame, partitioned storage - redirecting anyway strands
          // the user: /verify2FA answers AWAIT_CONFIRMATION with no redirect
          // URL, verify-2fa.js has no continuation to confirm with, and the
          // page just reloads itself.
          //
          // This is TERMINAL for the current attempt, not retryable, and the
          // distinction matters. By the time we get here the request above has
          // already moved the server transaction from UNBOUND to MFA_PENDING,
          // and /federated/link/password only accepts UNBOUND - so pressing
          // the button again with the same continuation cannot work. Telling
          // the user to "try again" would be a lie.
          //
          // Probing storage BEFORE the request was the other option and is
          // worse: we cannot know whether this account needs 2FA until the
          // server answers, so a pre-check would block every blocked-storage
          // user from linking, including the majority who never touch
          // sessionStorage on this path at all (completeGoogleLink does not
          // read it).
          //
          // So: clear the spent local state and send them back to the start,
          // which is the only recovery that actually works. Same terminal
          // treatment completeGoogleLink already uses for its dead-end cases.
          if (
            !setSessionItem("federatedLinkContinuation", googleLinkContinuation)
          ) {
            resetGoogleLink();
            setFederatedAlert(
              "Your browser is blocking site storage, so we couldn't complete two-factor verification. Allow site data for this site (or leave private browsing), then sign in with Google again.",
            );
            return;
          }
          return redirectFromFederatedResponse(link);
        }
        googleLinkReadyToConfirm = true;
        await completeGoogleLink();
      } catch (error) {
        const code = error?.payload?.error?.code;
        setGoogleLinkError(
          code === "SSO_ENFORCED"
            ? "This account requires SSO. Sign in with SSO instead."
            : error?.status === 401
            ? "That password is incorrect. Try again or reset your password."
            : "We couldn’t connect Google to your account. Please try again.",
        );
      } finally {
        googleLinkVerify.disabled = false;
        // The login-experiment modal (z94kydcrz3) carries its own label.
        googleLinkVerify.textContent =
          googleLinkVerify.dataset.idleLabel || "Connect Google and sign in";
        googleLinkVerify.setAttribute?.("aria-busy", "false");
      }
    });

    // Close, backdrop, and Escape all use Bootstrap's dismissal lifecycle.
    if (window.$)
      window.$(googleSignupPanel).on("hidden.bs.modal", resetGoogleSignup);
    googleSignupConfirm?.addEventListener("click", async () => {
      if (signupBusy) return;
      if (signupRestart) return startGoogle();
      if (!googleSignupContinuation && !googleSignupRecoveryUri) return;
      signupBusy = true;
      googleSignupConfirm.disabled = true;
      googleSignupConfirm.textContent = googleSignupRecoveryUri
        ? "Finishing account setup…"
        : "Creating your account…";
      googleSignupConfirm.setAttribute?.("aria-busy", "true");
      if (signupSwitch) signupSwitch.disabled = true;
      trackSignup(
        googleSignupRecoveryUri
          ? "google_signup_setup_retry_clicked"
          : "google_signup_create_clicked",
      );
      try {
        if (googleSignupRecoveryUri) {
          const recovered = await requestFederated(googleSignupRecoveryUri, {});
          signupBusy = false;
          signupExit = "completed";
          resetGoogleSignup();
          return redirectFromFederatedResponse(recovered);
        }
        // The page just reloaded after the OAuth round trip, so this page's
        // lookup may still be running. Give it a moment (it never rejects),
        // then go on: the pre-OAuth value and the server fall back.
        await Promise.race([
          prefetchAffiliateIp(),
          new Promise((resolve) => setTimeout(resolve, 1000)),
        ]);
        const signup = await requestFederated(
          "/oauth/v1/federated/google/signup",
          {
            continuation: googleSignupContinuation,
            termsAccepted: true,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            utmCode: collectSignupAttribution({
              storage: getLocalStorage(),
              location: window.location,
              // Never record the OAuth provider as the acquisition referrer.
              referrer: "",
              affiliateIp: getCachedAffiliateIp(),
            }),
          },
          { "GA-Client-Id": readGaClientId(document.cookie) || "" },
        );
        trackSignup("google_signup_account_created", { method: "google" });
        clearSignupAttribution(getLocalStorage());
        if (signup?.data?.provisioningRequired) {
          return showGoogleSignupRecovery(
            signup.data.retryUri || "/auth/local/resumeSignup",
          );
        }
        signupBusy = false;
        signupExit = "completed";
        resetGoogleSignup();
        redirectFromFederatedResponse(signup);
      } catch (error) {
        if (googleSignupRecoveryUri) {
          if (googleSignupError) {
            googleSignupError.textContent = signupRecoveryMessage(
              error?.status,
              error?.payload?.error?.code,
              error?.payload?.error?.retryAfterSeconds,
            );
            googleSignupError.classList.remove("d-none");
          }
        } else {
          const code = error?.payload?.error?.code;
          trackSignup("google_signup_create_failed", {
            error_code: code || "NETWORK_ERROR",
          });
          if (code === "LINK_REQUIRED") {
            googleLinkContinuation = error.payload.error.continuation;
            signupBusy = false;
            signupExit = "link_required";
            if (window.$)
              window
                .$(googleSignupPanel)
                .one("hidden.bs.modal", () =>
                  showGoogleLinkPanel(error.payload.error.email),
                );
            resetGoogleSignup();
            if (!window.$) showGoogleLinkPanel(error.payload.error.email);
          } else {
            // A timeout or server failure may follow a committed account creation.
            signupRestart =
              code === "SIGNUP_SESSION_EXPIRED" ||
              !error.status ||
              error.status >= 500 ||
              code === "SIGNUP_FAILED";
            if (googleSignupError) {
              googleSignupError.textContent =
                code === "SIGNUP_SESSION_EXPIRED"
                  ? "Your Google sign-in expired. Please sign in again to continue."
                  : signupRestart
                  ? "We couldn’t confirm whether your account was created. Sign in with Google again to continue."
                  : code === "SSO_ENFORCED"
                  ? "Your organization requires SSO. Sign in with SSO instead."
                  : "We couldn’t create your account. Please try again.";
              googleSignupError.classList.remove("d-none");
            }
          }
        }
      } finally {
        signupBusy = false;
        googleSignupConfirm.disabled = false;
        googleSignupConfirm.setAttribute?.("aria-busy", "false");
        googleSignupConfirm.textContent = signupRestart
          ? "Sign in with Google again"
          : googleSignupRecoveryUri
          ? "Retry account setup"
          : "Create account";
        if (signupSwitch) signupSwitch.disabled = false;
      }
    });
    const clearFederatedQuery = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("federated");
      url.searchParams.delete("code");
      window.history.replaceState(
        {},
        document.title,
        `${url.pathname}${url.search}${url.hash}`,
      );
    };
    const showFederatedLoginError = (code) =>
      setFederatedAlert(
        code === "SSO_ENFORCED"
          ? "This account requires SSO. Log in with SSO instead."
          : "Unable to sign in with Google. Please try again.",
      );
    const startGoogle = () => {
      // Capture before leaving the site; the callback no longer has campaign tags.
      collectSignupAttribution({
        storage: getLocalStorage(),
        location: window.location,
        referrer: returnedFromGoogle ? "" : document.referrer,
        affiliateIp: getCachedAffiliateIp(),
      });
      const params = new URLSearchParams();
      if (isGoogleSignupIntent) {
        params.set("intent", "signup");
        const billingType = getSignupBillingType(window.location.search) || googleProfile.billingType;
        if (billingType) params.set("billingType", billingType);
        const trialType =
          getExplicitSelfServeTrialType(window.location.search) ||
          googleProfile.trialType;
        if (trialType) params.set("trialType", trialType);
      }
      const query = params.toString();
      window.location.assign(
        `${HOST_URL}/oauth/v1/federated/google/start${
          query ? `?${query}` : ""
        }`,
      );
    };
    federatedGoogleButton.addEventListener("click", startGoogle);
    signupSwitch?.addEventListener("click", () => {
      if (signupBusy) return;
      trackSignup("google_signup_switch_account_clicked");
      startGoogle();
    });
    if (window.$) {
      window.$(googleSignupPanel).on("hide.bs.modal", (event) => {
        if (signupBusy) event.preventDefault();
      });
      window.$(googleSignupPanel).on("hidden.bs.modal", () => {
        if (!["link_required", "completed"].includes(signupExit)) {
          trackSignup("google_signup_dismissed", { exit: signupExit });
          federatedGoogleButton.focus?.();
        }
        signupExit = "close";
      });
      window
        .$(googleSignupPanel)
        .on("shown.bs.modal", () => googleSignupConfirm.focus?.());
      window.$(googleLinkPanel).on("hidden.bs.modal", () => {
        (googleLinkReturnFocus || federatedGoogleButton).focus?.();
        googleLinkReturnFocus = null;
      });
      window
        .$(googleLinkPanel)
        .on("shown.bs.modal", () => googleLinkPassword.focus?.());
    }
    [googleSignupPanel, googleLinkPanel].forEach((panel) => {
      panel?.addEventListener("keydown", (event) => {
        if (event.key !== "Tab") return;
        const controls = Array.from(
          panel.querySelectorAll(
            'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((control) => control.getClientRects().length > 0);
        if (!controls.length) return;
        const first = controls[0],
          last = controls[controls.length - 1];
        if (
          event.shiftKey &&
          (document.activeElement === first || document.activeElement === panel)
        ) {
          event.preventDefault();
          last.focus();
        } else if (
          !event.shiftKey &&
          (document.activeElement === last || document.activeElement === panel)
        ) {
          event.preventDefault();
          first.focus();
        }
      });
    });
    googleSignupPanel?.addEventListener("keydown", (event) => {
      if (event.key === "Escape") signupExit = "esc";
    });
    googleSignupPanel?.addEventListener("click", (event) => {
      if (event.target === googleSignupPanel) signupExit = "scrim";
    });
    if (googleLinkContinuation) completeGoogleLink();
    const federatedQuery = new URLSearchParams(window.location.search);
    if (federatedQuery.get("federated") === "resume") {
      requestFederated("/oauth/v1/federated/google/result")
        .then((response) => {
          clearFederatedQuery();
          const result = response?.data;
          // Deleted within the last 7 days: same "Reactivate your account?"
          // prompt the password login shows (local.js binds its button).
          if (
            result?.code === "ACCOUNT_SCHEDULED_FOR_DELETION" &&
            result.reactivateToken &&
            window.$
          ) {
            window
              .$("#forced-login")
              .attr("data-token", result.reactivateToken)
              .attr("data-continue", "google");
            window.$("#delete-user-modal").modal("show");
            return;
          }
          if (!result?.continuation)
            return showFederatedLoginError("SIGNIN_FAILED");
          googleProfile = result;
          if (result.code === "SIGNUP_REQUIRED") {
            renderGoogleProfile("signup", result);
            trackSignup("google_signup_modal_shown", { reason: "no_account" });
            googleSignupContinuation = result.continuation;
            if (googleSignupEmail)
              googleSignupEmail.textContent =
                result.email || "Your Google email";
            if (window.$) window.$(googleSignupPanel).modal("show");
            return;
          }
          if (result.code === "LINK_REQUIRED") {
            trackSignup("google_signup_modal_shown", {
              reason: "password_only",
            });
            googleLinkContinuation = result.continuation;
            showGoogleLinkPanel(result.email);
            return;
          }
          showFederatedLoginError("SIGNIN_FAILED");
        })
        .catch(() => {
          clearFederatedQuery();
          showFederatedLoginError("SIGNIN_FAILED");
        });
    } else if (federatedQuery.get("federated") === "cancelled") {
      clearFederatedQuery();
      federatedGoogleButton.focus?.();
    } else if (federatedQuery.get("federated") === "error") {
      const code = federatedQuery.get("code");
      clearFederatedQuery();
      showFederatedLoginError(code);
    }
  }

  const teamSocialLoginTriggers = document.querySelectorAll(
    "#team-social-auths button",
  );
  teamSocialLoginTriggers.forEach((btn) => {
    btn.addEventListener("click", onLogin.bind(null, true), false);
  });

  /**
   * @description Feature & Platform DropDown to be navigated on href on click & Drop Shown on hover
   */

  document.querySelectorAll(".dropdown-href-active").forEach((dropBtn) => {
    dropBtn.addEventListener("click", function (e) {
      e.preventDefault();
      window.location.href = this.href;
    });
  });

  function getUserInfo() {
    return new Promise((resolve, reject) => {
      const req = $.ajax({
        url: `${HOST_URL}/auth/getUserInfo`,
        method: "get",
        contentType: "application/json; charset=utf-8",
        dataType: "json",
      });

      req.done((e) => {
        if (e.status) {
          resolve(e);
        } else {
          reject();
        }
      });

      req.fail((e) => {
        reject();
      });
    });
  }

  $(() => {
    getUserInfo()
      .then((data) => {
        if (data.status) {
          $(".authentication").css({ display: "none" });
          $(".dashboard").css({ display: "" });
          $(".dashboard > a").attr("href", data.data.redirectUrl);
        } else {
          $(".authentication").css({ display: "" });
          $(".dashboard").css({ display: "none" });
        }
      })
      .catch((err) => {
        $(".authentication").css({ display: "" });
        $(".dashboard").css({ display: "none" });
      });
  });

  const checkCouponCode = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("couponCode") || params.has("lifetimeCode")) {
      $(".couponCodeAlert").removeClass("d-none");
    }
  };
  checkCouponCode();
};

export default init;
